import { Component, ChangeDetectionStrategy, input } from '@angular/core';
import { SeriesChartDisplay } from '../../../models/runnable_report';

import { computed, effect, ElementRef, inject, viewChild } from '@angular/core';
import { Chart, type ChartConfiguration, type ChartDataset, type ChartOptions, registerables } from 'chart.js';
import { ReportRunState } from '../report-run-state';
import { type ChartTypeLike } from '../chart-colors';
import 'chartjs-adapter-date-fns';
import { allPatterns, generateChartPalette } from '../chart-colors2';

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;


@Component({
    selector: 'risr-features-reports-report-series-chart',
    templateUrl: './report-series-chart.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        .chart-container {
            display: block;
            width: 100%;
            overflow-x: auto;
            background-color: white;
        }`
})
export class ReportSeriesChart {
    reportDisplayItem = input.required<SeriesChartDisplay>();

    private readonly reportRunState = inject(ReportRunState);

    protected readonly chartCanvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');

    private chart?: Chart<ChartTypeLike, (object | number)[], string>;
    private currentType?: ChartTypeLike;

    private readonly reportResult = computed(() => {
        const name = this.reportDisplayItem().output;
        return this.reportRunState.getOutputResult(name)();
    });

    constructor() {
        Chart.register(...registerables);

        effect(() => {
            const canvasRef = this.chartCanvas();
            const result = this.reportResult();
            const display = this.reportDisplayItem();

            if (!canvasRef?.nativeElement) {
                return;
            }
            if (!result) {
                this.destroyChart();
                return;
            }

            const config = this.buildChartConfig(display, result.headers, result.data);
            if (!config) {
                this.destroyChart();
                return;
            }

            if (!this.chart || this.currentType !== config.type) {
                this.destroyChart();
                this.chart = new Chart<ChartTypeLike, (object | number)[], string>(canvasRef.nativeElement, config);
                this.currentType = config.type;
                return;
            }

            this.chart.data = config.data;
            this.chart.options = config.options ?? {};
            this.chart.update();
        });
    }

    ngOnDestroy() {
        this.destroyChart();
    }

    private destroyChart() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = undefined;
        }
        this.currentType = undefined;
    }

    private buildChartConfig(
        display: SeriesChartDisplay,
        headers: { id: string; type: string }[],
        rows: unknown[][]
    ): ChartConfiguration<ChartTypeLike, (object | number)[], string> | undefined {
        const horizontalId = display.horizontal?.column;
        const verticals = display.vertical || [];
        if (!horizontalId || verticals.length === 0) {
            return undefined;
        }

        const headerIndex = headers.reduce((acc, h, index) => {
            acc.set(h.id, index);
            return acc;
        }, new Map<string, number>());

        const horizontalIndex = headerIndex.get(horizontalId);
        if (horizontalIndex === undefined) {
            return undefined;
        }


        const labels = rows.map((row) => this.formatLabel(row?.[horizontalIndex], headers[horizontalIndex]));

        const type = this.mapChartType(display.chartType);
        const isPieLike = type === 'pie' || type === 'doughnut' || type === 'polarArea' || type === 'radar';

        const datasets = verticals
            .map((v): ChartDataset<ChartTypeLike, (object | number)[]> | undefined => {
                const idx = headerIndex.get(v.column);
                if (idx === undefined) {
                    return undefined;
                }
                const data = rows.map((row, rowIdx) => {
                    if (isPieLike) {
                        return this.toNumber(row?.[idx]);
                    }
                    return {
                        x: labels[rowIdx],
                        y: this.toNumber(row?.[idx])
                    }
                });

                const needColorPalette = isPieLike || verticals.length === 1;
                const colors = generateChartPalette({
                    count: needColorPalette ? data.length : 1,
                    patterns: ['line', 'scatter'].includes(type) ? undefined : allPatterns,
                    seedHex: v.color || '#f29f05' // The default color is advance
                });

                return {
                    label: v.label || v.column,
                    data,
                    backgroundColor: colors
                };
            })
            .filter((d): d is ChartDataset<ChartTypeLike, (object | number)[]> => d !== undefined);

        const stacked = display.chartType === 'stackedBar' || display.chartType === 'stackedColumn';
        const isLineLike = type === 'line';
        const isTimeSeries = !isPieLike && headers[horizontalIndex].type?.toLowerCase().includes('date');

        const minType = display.options?.minType || 'auto';
        const maxType = display.options?.maxType || 'auto';
        const scaleMin = minType === 'auto' ? undefined : display.options?.min;
        const scaleMax = maxType === 'auto' ? undefined : display.options?.max;

        const scales = this.buildScales(type, {
            stacked,
            isTimeSeries,
            horizontalLabel: display.horizontalLabel,
            verticalLabel: display.verticalLabel,
            min: scaleMin,
            max: scaleMax,
        });

        const options: ChartOptions<ChartTypeLike> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: datasets.length > 1,
                },
                title: {
                    display: !!display.title,
                    text: display.title || '',
                },
            },
            scales,
        };

        if (display.chartType === 'area') {
            for (const ds of datasets) {
                (ds as any).fill = true;
            }
        }

        for (const ds of datasets) {
            if (isLineLike) {
                (ds as any).tension = 0.25;
                (ds as any).pointRadius = 2;
            }
        }

        return {
            type,
            data: {
                datasets,
                labels
            },
            options,
        };
    }

    private toNumber(value: unknown): number {
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        }
        return 0;
    }

    private formatLabel(value: unknown, header?: { id: string; type: string }): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return String(value);
        }
        if (value instanceof Date) {
            return value.toLocaleDateString();
        }
        if (header?.type?.toLowerCase().includes('date')) {
            const asString = String(value);
            const maybeDate = this.tryParseDate(asString);
            if (maybeDate) {
                return maybeDate.toLocaleDateString();
            }
        }
        return String(value);
    }

    /**
     * Strict ISO-8601 parser. Only `YYYY-MM-DD`, optionally followed by a time
     * part, is accepted so free text such as `2024` or `May` is never treated
     * as a date.
     */
    private tryParseDate(value: string): Date | undefined {
        const trimmed = value?.trim();
        if (!trimmed || !ISO_DATE_PATTERN.test(trimmed)) {
            return undefined;
        }
        const d = new Date(trimmed);
        if (Number.isNaN(d.getTime())) {
            return undefined;
        }
        const [year, month, day] = trimmed
            .slice(0, 10)
            .split('-')
            .map((part) => Number(part));
        const roundTrip =
            trimmed.length > 10
                ? { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() }
                : { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
        if (roundTrip.y !== year || roundTrip.m !== month || roundTrip.d !== day) {
            return undefined;
        }
        return d;
    }

    private mapChartType(type: string | undefined): ChartTypeLike {
        switch (type) {
            case 'pie':
                return 'pie';
            case 'donut':
                return 'doughnut';
            case 'radar':
                return 'radar';
            case 'polar':
                return 'polarArea';
            case 'line':
            case 'area':
                return 'line';
            case 'scatter':
                return 'scatter';
            case 'stackedColumn':
            case 'column':
            case 'bar':
            case 'stackedBar':
            default:
                return 'bar';
        }
    }

    private buildScales(
        type: ChartTypeLike,
        opts: {
            stacked: boolean;
            isTimeSeries: boolean;
            horizontalLabel?: string;
            verticalLabel?: string;
            min?: number;
            max?: number;
        }
    ): ChartOptions<ChartTypeLike>['scales'] {
        switch (type) {
            case 'pie':
            case 'doughnut':
                return {};
            case 'polarArea':
            case 'radar':
                return {
                    r: { min: opts.min, max: opts.max },
                };
            default:
                return {
                    x: {
                        title: { display: !!opts.horizontalLabel, text: opts.horizontalLabel },
                        stacked: opts.stacked,
                        type: opts.isTimeSeries ? 'time' : 'category',
                        ticks: { autoSkip: true, maxRotation: 45, minRotation: 0 },
                    },
                    y: {
                        title: { display: !!opts.verticalLabel, text: opts.verticalLabel },
                        stacked: opts.stacked,
                        beginAtZero: true,
                        min: opts.min,
                        max: opts.max,
                    },
                };
        }
    }
}
